import type { MapiStory } from '@storyblok/openapi'
import { STORY_STATUS_OPTIONS } from '@constants/storyStatus'
import type { StoryStatus } from '@constants/storyStatus'
import type { SpaceOption } from '@/types'

type LangPublishedStatus = {
  publishedChanges: boolean
  published: boolean
}

const langIsPublished = (
  story: Pick<MapiStory, 'translated_stories'>,
  lang: string,
): LangPublishedStatus => {
  if (!story.translated_stories) {
    return {
      publishedChanges: false,
      published: false,
    }
  }

  const translations = story.translated_stories.filter((translation) => {
    return translation.lang === lang
  })
  const hasTranslations = translations.length > 0

  return {
    publishedChanges:
      hasTranslations && Boolean(translations[0]?.unpublished_changes),
    published: hasTranslations,
  }
}

const expired = (story: Pick<MapiStory, 'expire_at'>): boolean => {
  return story.expire_at ? new Date(story.expire_at) < new Date() : false
}

const scheduled = (story: Pick<MapiStory, 'publish_at'>): boolean => {
  return story.publish_at ? new Date(story.publish_at) > new Date() : false
}

const getStatusObject = (
  story: Pick<
    MapiStory,
    | 'translated_stories'
    | 'published'
    | 'unpublished_changes'
    | 'expire_at'
    | 'publish_at'
    | 'is_scheduled'
  >,
  lang: string,
  spaceOptions: Pick<SpaceOption, 'use_translated_stories'>,
): StoryStatus => {
  if (
    lang !== '' &&
    spaceOptions.use_translated_stories &&
    story.translated_stories
  ) {
    const langPublishedStatus = langIsPublished(story, lang)
    if (langPublishedStatus.publishedChanges) {
      return STORY_STATUS_OPTIONS.published_has_changes
    }
    if (langPublishedStatus.published) {
      return STORY_STATUS_OPTIONS.published
    }
    return STORY_STATUS_OPTIONS.draft
  }

  if (
    !story.published &&
    !('is_scheduled' in story ? story.is_scheduled : false)
  ) {
    return STORY_STATUS_OPTIONS.draft
  }

  let status: StoryStatus = STORY_STATUS_OPTIONS.published

  if (expired(story)) {
    status = STORY_STATUS_OPTIONS.expired
  }

  if (('is_scheduled' in story && story.is_scheduled) || scheduled(story)) {
    status = STORY_STATUS_OPTIONS.scheduled
  }

  if (story.unpublished_changes) {
    return STORY_STATUS_OPTIONS.published_has_changes
  }

  return status
}

export const getStatus = (
  story: Pick<
    MapiStory,
    | 'translated_stories'
    | 'published'
    | 'unpublished_changes'
    | 'expire_at'
    | 'publish_at'
    | 'is_scheduled'
  >,
  lang: string,
  spaceOptions: Pick<SpaceOption, 'use_translated_stories'>,
): StoryStatus => {
  return getStatusObject(story, lang, spaceOptions)
}
